import { Close, Favorite } from '@mui/icons-material';
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React from 'react';
import { IntlShape } from 'react-intl';
import { assign } from 'xstate';

import { MachineContext } from '../../MachineContext';
import theme from '../../theme/theme';
import { translateWithFallback } from '../../util';
import { EvoachButton } from '../Button';
import { BaseComponent } from '../Component';
import { CardImageEnum, CardImageSize, ObjectCardImage } from '../ObjectCard';

import { ValueCardSize } from './ValueCardSizeEnums';

export interface ValueCardComponent extends BaseComponent {
  type: 'valueCard';
  props: ValueCardComponentProps;
}

//
// generic Card Props
//
// we use 'value' instead of 'title' as prop name
// because we use this interface also in the typedValue
// object in builder+player for inter-component usage. And this inter-component usage
// is not typed, so we require at least type and value. Value is usually the value to
// with a text.
export interface ValueCardProps {
  value: string; // needed for inter-component data excahnge
  type: string; // needed for inter-component data excahnge
  description?: string;
  /**
   * @deprecated use imageSrc in ValueCardComponentProps instead
   */
  image?: CardImageEnum;
  imageSrc?: string;
  skipButtonText?: string;
  liked?: boolean;
  isEcho?: boolean; // PROD-1371
}

// component Props
export interface ValueCardComponentProps extends ValueCardProps {
  id?: string;
  imageSize?: CardImageSize;
  cardSize?: ValueCardSize;
  cardNumber?: number;
  cardAmount?: number;
  onClickLike?: () => void;
  onClickDislike?: () => void;
  onClickSkip?: () => void;
}

/**
 *
 * @param value defines the value
 * @param description explains the value in greater detail
 * @param saveResultTo saves 'add', 'remove' or 'skip' for the value
 * @param id property to identify the card
 * @param image defines the displayed image as CardImageEnum
 * @param imageSrc mapped path for image
 * @param imageSize defines the size of the image as CardImageSize
 * @param cardSize defines the size of the card as CardSize
 * @param cardNumber shows which number this card is in a series
 * @param cardAmount shows total amount of cards in a series
 * @param skipButtonText defines text of the skip button
 * @param {boolean} isEcho - optional - if true, numbering and buttons are not displayed
 * @constructor
 */
export const ValueCard: React.FC<ValueCardComponentProps> = ({
  value,
  description,
  id,
  image,
  imageSrc,
  imageSize = CardImageSize.LARGE,
  cardSize = description ? ValueCardSize.LARGE : ValueCardSize.SMALL,
  cardNumber,
  cardAmount,
  skipButtonText,
  onClickLike,
  onClickDislike,
  onClickSkip,
  isEcho = false,
}) => {
  const displayDescription =
    description !== undefined && description.length > 0;
  const title = value;
  /*
  // found here: https://dev.to/benjaminadk/parse-user-input-for-urls-timestamps--hashtags-3dh4
  const hashtags = description.match(/\B#[a-z][a-z0-9._-]*\b/gi);
  console.log(hashtags);

  const parsed = description.replaceAll(
    /\B#[a-z][a-z0-9._-]*\b/gi,
    ''
  );
  */

  const baseHeight = !isEcho ? cardSize : cardSize - 130;
  const descriptionHeight = cardSize === ValueCardSize.LARGE ? 80 : 55;
  const height =
    baseHeight -
    (displayDescription ? 0 : 50) -
    145 +
    (image || imageSrc ? imageSize : 0) +
    'px';

  const widthNum =
    (cardSize === ValueCardSize.LARGE ? 275 : 220) -
    (displayDescription ? 0 : 50);

  const width = widthNum + 'px';

  return (
    <Card
      sx={{
        width: width,
        maxWidth: width,
        height: height,
        maxHeight: height,
        overflow: 'hidden',
      }}
      id={id + ''}
    >
      {!isEcho && (
        <CardContent sx={{ paddingBottom: '0px' }}>
          <Typography variant="body2" color="text.secondary" component="div">
            {cardNumber !== undefined || cardAmount !== undefined
              ? `${cardNumber}/${cardAmount}`
              : ''}
          </Typography>
        </CardContent>
      )}
      {imageSrc && (
        <CardContent
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0px',
            paddingBottom: '10px',
            paddingTop: '10px',
          }}
        >
          <ObjectCardImage imageSrc={imageSrc} imageSize={imageSize} />
        </CardContent>
      )}
      <CardContent>
        <Typography
          align="center"
          variant="h6"
          fontWeight={600}
          lineHeight="normal"
          component="div"
          style={{ marginTop: '-5px', height: '55px', overflow: 'hidden' }}
        >
          {title}
        </Typography>

        {displayDescription && (
          <Typography
            align="center"
            variant="body2"
            color="text.secondary"
            component="div"
            style={{
              marginTop: '5px',
              height: descriptionHeight + 'px',
              overflowY: 'auto',
            }}
          >
            {description}
          </Typography>
        )}
      </CardContent>
      {!isEcho && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '3rem',
          }}
        >
          <IconButton
            size="large"
            sx={{
              borderColor: theme.palette.secondary.main,
              borderStyle: 'solid',
              borderWidth: 'thin',
            }}
            onClick={onClickLike}
          >
            <Favorite />
          </IconButton>
          <IconButton
            size="large"
            sx={{
              borderColor: theme.palette.secondary.main,
              borderStyle: 'solid',
              borderWidth: 'thin',
            }}
            onClick={onClickDislike}
          >
            <Close />
          </IconButton>
        </Box>
      )}
      {skipButtonText ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '25px',
          }}
        >
          <EvoachButton variant="text" size="small" onClick={onClickSkip}>
            {skipButtonText}
          </EvoachButton>
        </Box>
      ) : (
        ''
      )}
    </Card>
  );
};

export const renderValueCardAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;

    newContext.widgetData.push({
      type: 'valueCard',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        type: payload.type,
        value: translateWithFallback(intl, payload.value),
        description: translateWithFallback(intl, payload.description),
        id: payload.id,
        image: payload.image,
        imageSrc: payload.imageSrc,
        imageSize: payload.imageSize,
        cardSize: payload.cardSize,
        cardNumber: payload.cardNumber,
        cardAmount: payload.cardAmount,
        skipButtonText: translateWithFallback(intl, payload.skipButtonText),
      },
    });
    return newContext;
  });
};
